import './index.css';
import { jsx } from 'react/jsx-runtime';
import { forwardRef, useRef, useLayoutEffect } from 'react';
import { keyboardMatches, keyboardKeys } from '@hh.ru/magritte-common-keyboard';
import { useDisabled } from '@hh.ru/magritte-common-use-disabled';
import { CheckableCardElement } from './CheckableCardElement.js';
import { CheckableCardSkeleton } from './Skeleton.js';
import { useScrollContext } from '@hh.ru/magritte-ui-scrollable-container';
import { enrichWithSkeleton } from '@hh.ru/magritte-ui-skeleton';
import 'classnames';
import './checkable-card-DJrOyTI9.js';
import '@hh.ru/magritte-common-use-loading-state';

const CheckableCardComponent = forwardRef(({ children, padding = 0, paddingLeft, paddingRight, paddingTop, paddingBottom, borderRadius = 0, borderTopLeftRadius, borderTopRightRadius, borderBottomLeftRadius, borderBottomRightRadius, toneHover, onFocus, onClick, disabled, type = 'checkbox', flexible, labelDataQa, ...inputProps }, ref) => {
    const contentRef = useRef(null);
    const inputRef = useRef(null);
    const forceDisabled = useDisabled();
    const { registerItem, removeItem, scrollToItem } = useScrollContext();
    const stopKeyboardEventPropagation = (event) => {
        if (event.target === inputRef.current ||
            !keyboardMatches(event, [keyboardKeys.Space, keyboardKeys.Enter])) {
            return;
        }
        event.stopPropagation();
        inputRef.current?.dispatchEvent(new KeyboardEvent(event.type, { key: event.key, code: event.code }));
    };
    useLayoutEffect(() => {
        registerItem(contentRef);
        return () => removeItem(contentRef);
    }, [registerItem, removeItem]);
    useLayoutEffect(() => {
        if ((inputProps.checked || inputProps.defaultChecked) && type === 'radio' && contentRef.current) {
            scrollToItem(contentRef);
        }
        // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
    }, []);
    const props = {
        padding,
        paddingLeft,
        paddingRight,
        paddingTop,
        paddingBottom,
        borderRadius,
        borderTopLeftRadius,
        borderTopRightRadius,
        borderBottomLeftRadius,
        borderBottomRightRadius,
        disabled,
        toneHover,
        flexible,
        'data-qa': labelDataQa,
    };
    return (jsx(CheckableCardElement, { ...props, onClickCapture: (event) => {
            if (event.target === inputRef.current || !contentRef.current?.contains(event.target)) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            inputRef.current?.click();
        }, padding: padding, borderRadius: borderRadius, onKeyDownCapture: stopKeyboardEventPropagation, onKeyPressCapture: stopKeyboardEventPropagation, onKeyUpCapture: stopKeyboardEventPropagation, input: jsx("input", { ...inputProps, ref: inputRef, type: type, disabled: !!disabled || forceDisabled, onFocus: (event) => {
                onFocus?.(event);
                scrollToItem(contentRef);
            }, onClick: (event) => {
                onClick?.(event);
                scrollToItem(contentRef);
            } }), Element: "label", ref: ref, contentRef: contentRef, disabled: !!disabled || forceDisabled, children: children }));
});
CheckableCardComponent.displayName = 'CheckableCard';
const CheckableCard = enrichWithSkeleton(CheckableCardComponent, CheckableCardSkeleton);

export { CheckableCard, CheckableCardComponent };
//# sourceMappingURL=CheckableCard.js.map
